import algoliasearch from 'algoliasearch/lite'

(function () {
  "use strict";

  const defaults = window.CampsUtils?.defaults || {};

  const CampsNearYouCampBrand = {
    config: {
      fetchCount: 5,    // Fetch 5 to skip first (current camp)
      displayCount: 4,  // Show 4 camps
      algoliaAppId: defaults.algoliaAppId,
      algoliaSearchKey: defaults.algoliaSearchKey,
      algoliaIndex: defaults.algoliaIndex,
      // Easy toggle: set to true to also exclude current sport in fallback mode
      excludeCurrentSport: true,
    },

    elements: {
      container: null,
      list: null,
      title: null,
    },

    index: null,

    campData: {
      lat: null,
      lng: null,
      brandSlug: null,
      sportSlug: null,
      campUrl: null,
    },

    init: function () {
      this.elements.container = document.querySelector(
        '.rd-camps-near-you[data-page="camp-brand"]'
      );
      if (!this.elements.container) return;

      this.elements.list = this.elements.container.querySelector(".rd-camps-near-you__list");
      this.elements.title = this.elements.container.querySelector(".rd-camps-near-you__title");

      const dataset = this.elements.container.dataset;
      this.campData.lat = parseFloat(dataset.campLat);
      this.campData.lng = parseFloat(dataset.campLng);
      this.campData.brandSlug = dataset.brand || null;
      this.campData.sportSlug = dataset.campSport || null;
      this.campData.campUrl = dataset.campUrl || null;

      if (!this.campData.lat || !this.campData.lng) {
        console.error("[CampsNearYouCampBrand] Invalid coordinates:", this.campData.lat, this.campData.lng);
        this.handleError();
        return;
      }

      if (!this.campData.brandSlug) {
        console.error("[CampsNearYouCampBrand] Missing data-brand attribute");
        this.handleError();
        return;
      }

      console.log(
        "[CampsNearYouCampBrand] Initializing — brand:", this.campData.brandSlug,
        "coords:", this.campData.lat, this.campData.lng
      );

      this.observeAndLoad();
    },

    /**
     * Defer Algolia request until the section enters the viewport.
     * Saves Algolia operations for users who never scroll to the bottom of the camp page.
     */
    observeAndLoad: function () {
      if (!("IntersectionObserver" in window)) {
        this.waitForAlgoliaAndLoad();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              observer.disconnect();
              this.waitForAlgoliaAndLoad();
              break;
            }
          }
        },
        { rootMargin: "200px 0px", threshold: 0 }
      );

      observer.observe(this.elements.container);
    },

    waitForAlgoliaAndLoad: function () {
      const maxAttempts = 50;
      let attempts = 0;

      const checkAlgolia = () => {
        if (typeof algoliasearch !== "undefined") {
          const client = algoliasearch(
            this.config.algoliaAppId,
            this.config.algoliaSearchKey,
          )
          this.index = client.initIndex(this.config.algoliaIndex)
          this.loadCamps();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkAlgolia, 100);
        } else {
          console.error("[CampsNearYouCampBrand] Algolia failed to load after 5s");
          this.handleError();
        }
      };

      checkAlgolia();
    },

    loadCamps: async function () {
      try {
        // Step 1: Try brand-filtered results
        const brandHits = await this.fetchByBrand();
        const brandCamps = this.filterAndLimit(brandHits);

        if (brandCamps.length > 0) {
          console.log("[CampsNearYouCampBrand] Brand results:", brandCamps.length);
          this.renderCamps(brandCamps);
          return;
        }

        // Step 2: Fallback — standard nearby logic (no brand filter, exclude current sport)
        console.log("[CampsNearYouCampBrand] No brand results → falling back to standard nearby");
        const fallbackHits = await this.fetchStandardNearby();
        const fallbackCamps = this.filterAndLimit(fallbackHits);

        if (fallbackCamps.length > 0) {
          this.switchToStandardMode();
          this.renderCamps(fallbackCamps);
        } else {
          this.handleNoCamps();
        }
      } catch (error) {
        console.error("[CampsNearYouCampBrand] ERROR:", error);
        this.handleError();
      }
    },

    filterAndLimit: function (hits) {
      return hits
        .filter((hit) => !this.campData.campUrl || hit.url !== this.campData.campUrl)
        .slice(0, this.config.displayCount);
    },

    fetchByBrand: async function () {
      const brandName = this.slugToBrandName(this.campData.brandSlug);

      const { hits } = await this.index.search("", {
        aroundLatLng: `${this.campData.lat},${this.campData.lng}`,
        hitsPerPage: this.config.fetchCount,
        filters: `brands:"${brandName}" AND sessionsCount > 0`,
        getRankingInfo: true,
      });

      return hits;
    },

    fetchStandardNearby: async function () {
      const filters = [
        "sessionsCount > 0",
        this.config.excludeCurrentSport && this.campData.sportSlug
          ? `NOT sportSlug:"${this.campData.sportSlug}"`
          : null,
      ]
        .filter(Boolean)
        .join(" AND ");

      const { hits } = await this.index.search("", {
        aroundLatLng: `${this.campData.lat},${this.campData.lng}`,
        hitsPerPage: this.config.fetchCount,
        filters: filters,
        getRankingInfo: true,
      });

      return hits;
    },

    /**
     * Switch section to standard (non-brand) mode:
     * - Update title
     * - Add CSS class that resets brand color vars back to orange
     */
    switchToStandardMode: function () {
      if (this.elements.title) {
        this.elements.title.textContent = "Other Camps You might Like";
      }
      if (this.elements.container) {
        this.elements.container.classList.add("rd-camps-near-you--standard");
      }
      console.log("[CampsNearYouCampBrand] Switched to standard mode");
    },

    /**
     * Convert brand slug to brand name for Algolia filter
     * Example: "nbc-camps" -> "NBC Camps", "nike" -> "Nike"
     */
    slugToBrandName: function (slug) {
      if (!slug) return "";

      return slug
        .split("-")
        .map((word) => {
          if (word.length >= 2 && word.length <= 4) {
            return word.toUpperCase();
          }
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(" ");
    },

    renderCamps: function (camps) {
      if (!this.elements.list) return;

      const html = camps
        .map((camp) => {
          const showDistance =
            typeof camp._rankingInfo?.geoDistance === "number";
          return window.CampsUtils?.renderCampCardHtml(camp, { showDistance });
        })
        .join("");

      this.elements.list.innerHTML = html;
      pushGTMViewItemList(camps, false, null);
      selectItemTracking(camps, null)
    },

    handleNoCamps: function () {
      if (this.elements.container) {
        this.elements.container.style.display = "none";
      }
    },

    handleError: function () {
      console.warn("[CampsNearYouCampBrand] Error — hiding section");
      if (this.elements.container) {
        this.elements.container.style.display = "none";
      }
    },
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => CampsNearYouCampBrand.init());
  } else {
    CampsNearYouCampBrand.init();
  }
})();
